import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
   Payment,
  History,
  AccountBalance,
  Send,
  LocationOn,
  Close,
  EmojiEvents,
  Celebration,
  Star,
} from '@mui/icons-material';
import * as colors from '../config/colorProfile';

// Achievement Poster Component
const AchievementPoster = ({ type = 'achievement', message, subtitle, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const configs = {
    achievement: {
      gradient: colors.ACHIEVEMENT_GRADIENT_1,
      icon: EmojiEvents,
      emoji: '🏆',
      title: 'Congratulations!',
    },
    target: {
      gradient: colors.ACHIEVEMENT_GRADIENT_2,
      icon: Star,
      emoji: '⭐',
      title: 'Target Achieved!',
    },
    celebration: {
      gradient: colors.ACHIEVEMENT_GRADIENT_3,
      icon: Celebration,
      emoji: '🎉',
      title: 'Great Job!',
    },
  };

  const config = configs[type] || configs.achievement;
  const IconComponent = config.icon;

  return (
    <Card
      sx={{
        background: config.gradient,
        borderRadius: 3,
        mb: 2,
        position: 'relative',
        overflow: 'visible',
        boxShadow: colors.SHADOW_XL,
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3, position: 'relative' }}>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'rgba(255,255,255,0.8)',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              },
            }}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: -20,
            left: 20,
            fontSize: 60,
            opacity: 0.15,
            transform: 'rotate(-15deg)',
          }}
        >
          {config.emoji}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            right: 30,
            fontSize: 80,
            opacity: 0.1,
            transform: 'rotate(15deg)',
          }}
        >
          {config.emoji}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1.5,
              display: 'inline-flex',
              backdropFilter: 'blur(10px)',
            }}
          >
            <IconComponent sx={{ color: 'white', fontSize: isMobile ? 28 : 36 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                color: 'white',
                fontWeight: 800,
                textShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}
            >
              {config.title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: 'white',
            fontWeight: 600,
            lineHeight: 1.6,
            textShadow: '0 1px 4px rgba(0,0,0,0.15)',
            fontSize: isMobile ? 14 : 15,
          }}
        >
          {message}
        </Typography>

        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 16,
              animation: 'sparkle 2s infinite',
              animationDelay: `${i * 0.4}s`,
              top: `${20 + i * 15}%`,
              right: `${10 + i * 15}%`,
              '@keyframes sparkle': {
                '0%, 100%': { opacity: 0, transform: 'scale(0)' },
                '50%': { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            ✨
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    todayCollection: 8450,
    totalCustomers: 156,
    cashInHand: 6450,
    pendingSubmission: 2000,
    monthlyTarget: 150000,
    monthlyAchieved: 98500,
    weeklyTarget: 30000,
    weeklyAchieved: 22400,
    dailyTarget: 5000,
    dailyAchieved: 8450,
    weeklyGrowth: 12.5,
  });

  const [recentTransactions] = useState([
    {
      id: 1,
      customerName: 'Rajesh Kumar',
      amount: 2500,
      time: '2 hours ago',
      status: 'completed',
    },
    {
      id: 2,
      customerName: 'Priya Sharma',
      amount: 1800,
      time: '3 hours ago',
      status: 'completed',
    },
    {
      id: 3,
      customerName: 'Amit Patel',
      amount: 3200,
      time: '5 hours ago',
      status: 'completed',
    },
    {
      id: 4,
      customerName: 'Sunita Reddy',
      amount: 950,
      time: 'Yesterday',
      status: 'completed',
    },
  ]);

  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: null,
    loading: false,
    error: null,
  });

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [showAchievement, setShowAchievement] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/dashboard/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getGPSLocation = () => {
    setLocation((prev) => ({ ...prev, loading: true, error: null }));

    if (!navigator.geolocation) {
      setLocation((prev) => ({
        ...prev,
        loading: false,
        error: 'Geolocation is not supported by your browser',
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          const address =
            data.address?.city || data.address?.town || 'Unknown Location';

          setLocation({
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
            address,
            loading: false,
            error: null,
          });
        } catch (err) {
          setLocation({
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
            address: 'Address not available',
            loading: false,
            error: null,
          });
        }
      },
      (error) => {
        setLocation((prev) => ({
          ...prev,
          loading: false,
          error: `Error: ${error.message}`,
        }));
      }
    );
  };

  const { dailyPercentage, weeklyPercentage, monthlyPercentage } = useMemo(() => {
    const safePercent = (achieved, target) =>
      target ? ((achieved / target) * 100).toFixed(1) : '0.0';

    return {
      dailyPercentage: safePercent(stats.dailyAchieved, stats.dailyTarget),
      weeklyPercentage: safePercent(stats.weeklyAchieved, stats.weeklyTarget),
      monthlyPercentage: safePercent(stats.monthlyAchieved, stats.monthlyTarget),
    };
  }, [stats]);

  const cardBaseSx = {
    borderRadius: 3,
    boxShadow: colors.CARD_SHADOW,
    border: `1px solid ${colors.BORDER_COLOR}`,
    bgcolor: colors.CARD_BG,
  };

  const compactLabelSx = {
    fontSize: 12,
    fontWeight: 500,
    color: colors.TEXT_SECONDARY,
  };

  const compactValueSx = {
    fontSize: 16,
    fontWeight: 700,
    color: colors.TEXT_PRIMARY,
  };

  const getAchievementMessage = () => {
    const dailyPct = Number(dailyPercentage);
    if (dailyPct >= 100) {
      return {
        type: 'target',
        message: `You've exceeded your daily target by ${(dailyPct - 100).toFixed(1)}%! Outstanding performance!`,
        subtitle: 'Daily Target Achievement',
      };
    } else if (dailyPct >= 90) {
      return {
        type: 'celebration',
        message: `You're at ${dailyPct}% of your daily target. Just a little more to go!`,
        subtitle: 'Almost There!',
      };
    } else {
      return {
        type: 'celebration',
        message: `Welcome! You're making great progress today. Keep up the excellent work!`,
        subtitle: 'Stay Motivated',
      };
    }
  };

  const achievementData = getAchievementMessage();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.DASHBOARD_BG,
        py: 2,
      }}
    >
      <Container maxWidth="md">
        {/* Top: Greeting + Location */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ color: colors.TEXT_PRIMARY, fontWeight: 700, mb: 0.5 }}
            >
              Welcome back 👋
            </Typography>
            <Typography variant="body2" sx={{ color: colors.TEXT_SECONDARY }}>
              Today's performance summary
            </Typography>
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={<LocationOn />}
            onClick={() => setLocationDialogOpen(true)}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: colors.BORDER_COLOR,
              color: colors.TEXT_PRIMARY,
              '&:hover': {
                borderColor: colors.PRIMARY_COLOR,
                bgcolor: colors.TOPBAR_HOVER,
              },
            }}
          >
            {isMobile ? 'Location' : 'Current'}
          </Button>
        </Box>

        {/* Achievement Poster */}
        {showAchievement && (
          <AchievementPoster
            type={achievementData.type}
            message={achievementData.message}
            subtitle={achievementData.subtitle}
            onClose={() => setShowAchievement(false)}
          />
        )}

        {/* Hero card with main KPIs */}
        <Card sx={{ ...cardBaseSx, mb: 2 }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.TEXT_SECONDARY, fontWeight: 500 }}
                >
                  Today's Collection
                </Typography>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 800,
                    color: colors.TEXT_PRIMARY,
                    mt: 0.5,
                  }}
                >
                  ₹{stats.dailyAchieved.toLocaleString()}
                </Typography>
              </Box>
              <Chip
                label={`${dailyPercentage}% of target`}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: colors.STATUS_SUCCESS_BG,
                  color: colors.getProgressColor(dailyPercentage),
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            </Box>

            <LinearProgress
              variant="determinate"
              value={Math.min(Number(dailyPercentage) || 0, 100)}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: colors.PROGRESS_BG,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor: colors.getProgressColor(dailyPercentage),
                },
                mb: 2,
              }}
            />

            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <Typography sx={compactLabelSx}>Target</Typography>
                <Typography sx={compactValueSx}>
                  ₹{stats.dailyTarget.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={compactLabelSx}>Cash in hand</Typography>
                <Typography sx={compactValueSx}>
                  ₹{stats.cashInHand.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={compactLabelSx}>Pending submit</Typography>
                <Typography sx={{ ...compactValueSx, color: colors.ERROR_COLOR }}>
                  ₹{stats.pendingSubmission.toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Targets row */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={cardBaseSx}>
              <CardContent sx={{ p: 2 }}>
                <Typography sx={compactLabelSx}>Daily</Typography>
                <Typography sx={compactValueSx}>
                  {dailyPercentage}% (₹{stats.dailyAchieved.toLocaleString()})
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Number(dailyPercentage) || 0, 100)}
                  sx={{
                    mt: 1,
                    height: 5,
                    borderRadius: 999,
                    bgcolor: colors.PROGRESS_BG,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: colors.getProgressColor(dailyPercentage),
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={cardBaseSx}>
              <CardContent sx={{ p: 2 }}>
                <Typography sx={compactLabelSx}>Weekly</Typography>
                <Typography sx={compactValueSx}>
                  {weeklyPercentage}% (₹{stats.weeklyAchieved.toLocaleString()})
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Number(weeklyPercentage) || 0, 100)}
                  sx={{
                    mt: 1,
                    height: 5,
                    borderRadius: 999,
                    bgcolor: colors.PROGRESS_BG,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: colors.getProgressColor(weeklyPercentage),
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={cardBaseSx}>
              <CardContent sx={{ p: 2 }}>
                <Typography sx={compactLabelSx}>Monthly</Typography>
                <Typography sx={compactValueSx}>
                  {monthlyPercentage}% (₹{stats.monthlyAchieved.toLocaleString()})
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Number(monthlyPercentage) || 0, 100)}
                  sx={{
                    mt: 1,
                    height: 5,
                    borderRadius: 999,
                    bgcolor: colors.PROGRESS_BG,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: colors.getProgressColor(monthlyPercentage),
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Actions + Recent */}
        <Grid container spacing={2}>
          {/* Quick actions */}
          <Grid item xs={12} md={5}>
            <Card sx={cardBaseSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1.5, color: colors.TEXT_PRIMARY }}
                >
                  Quick actions
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      onClick={() => navigate('/add-collection')}
                      sx={{
                        borderRadius: 2.5,
                        px: 1,
                        py: 1.5,
                        minHeight: 80,
                        bgcolor: colors.ACTION_BLUE_BG,
                        color: colors.ACTION_BLUE_TEXT,
                        textTransform: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        '&:hover': {
                          bgcolor: colors.ACTION_BLUE_HOVER,
                        },
                      }}
                    >
                      <Payment fontSize="small" />
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                        Add
                      </Typography>
                    </Button>
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      onClick={() => navigate('/history')}
                      sx={{
                        borderRadius: 2.5,
                        px: 1,
                        py: 1.5,
                        minHeight: 80,
                        bgcolor: colors.ACTION_GREEN_BG,
                        color: colors.ACTION_GREEN_TEXT,
                        textTransform: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        '&:hover': {
                          bgcolor: colors.ACTION_GREEN_HOVER,
                        },
                      }}
                    >
                      <History fontSize="small" />
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                        History
                      </Typography>
                    </Button>
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      onClick={() => navigate('/cashbook')}
                      sx={{
                        borderRadius: 2.5,
                        px: 1,
                        py: 1.5,
                        minHeight: 80,
                        bgcolor: colors.ACTION_YELLOW_BG,
                        color: colors.ACTION_YELLOW_TEXT,
                        textTransform: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        '&:hover': {
                          bgcolor: colors.ACTION_YELLOW_HOVER,
                        },
                      }}
                    >
                      <AccountBalance fontSize="small" />
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                        Cash book
                      </Typography>
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/cash-management')}
                      startIcon={<Send />}
                      sx={{
                        mt: 0.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                        borderColor: colors.BORDER_COLOR,
                        color: colors.PRIMARY_COLOR,
                        '&:hover': {
                          borderColor: colors.PRIMARY_COLOR,
                          bgcolor: colors.TOPBAR_HOVER,
                        },
                      }}
                    >
                      Cash management
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent activity */}
          <Grid item xs={12} md={7}>
            <Card sx={cardBaseSx}>
              <CardContent sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY }}
                  >
                    Recent activity
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/history')}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.PRIMARY_COLOR,
                    }}
                  >
                    View all
                  </Button>
                </Box>

                {recentTransactions.slice(0, 4).map((t, idx) => (
                  <Box key={t.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: colors.ACTION_BLUE_BG,
                            color: colors.ACTION_BLUE_TEXT,
                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          {t.customerName[0]}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: colors.TEXT_PRIMARY,
                            }}
                          >
                            {t.customerName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: colors.TEXT_SECONDARY,
                            }}
                          >
                            {t.time}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: colors.SUCCESS_COLOR,
                          }}
                        >
                          +₹{t.amount.toLocaleString()}
                        </Typography>
                        <Chip
                          label={t.status}
                          size="small"
                          sx={{
                            mt: 0.3,
                            height: 18,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: colors.STATUS_SUCCESS_BG,
                            color: colors.STATUS_SUCCESS_TEXT,
                          }}
                        />
                      </Box>
                    </Box>
                    {idx < recentTransactions.slice(0, 4).length - 1 && (
                      <Divider sx={{ opacity: 0.4 }} />
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Location dialog */}
        <Dialog
          open={locationDialogOpen}
          onClose={() => setLocationDialogOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: colors.PRIMARY_COLOR,
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Current location
            </Typography>
            <IconButton
              onClick={() => setLocationDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {location.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {location.error}
              </Alert>
            )}

            {location.latitude && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={compactLabelSx}>Latitude</Typography>
                  <Typography sx={compactValueSx}>{location.latitude}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={compactLabelSx}>Longitude</Typography>
                  <Typography sx={compactValueSx}>{location.longitude}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={compactLabelSx}>Address</Typography>
                  <Typography sx={compactValueSx}>{location.address}</Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: colors.STATUS_INFO_BG,
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: colors.STATUS_INFO_TEXT }}>
                    📍 Fetched at {new Date().toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            )}

            {!location.latitude && !location.error && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocationOn sx={{ fontSize: 42, color: colors.TEXT_DISABLED, mb: 1 }} />
                <Typography variant="body2" color={colors.TEXT_SECONDARY}>
                  Tap "Get location" to fetch your current GPS coordinates
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setLocationDialogOpen(false)}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                color: colors.TEXT_SECONDARY
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<LocationOn />}
              onClick={getGPSLocation}
              disabled={location.loading}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                bgcolor: colors.PRIMARY_COLOR
              }}
            >
              {location.loading ? 'Getting location…' : 'Get location'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Dashboard;
